import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Shield, Plus, Trash2, Users, Key, ArrowLeft } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface InviteCode {
  code: string;
  created_by: string | null;
  used_by: string | null;
  used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

interface UserRecord {
  id: string;
  github_id: string | null;
  github_login: string | null;
  avatar_url: string | null;
  display_name: string | null;
  status: string;
  created_at: string;
  last_login: string | null;
}

async function adminFetch<T>(path: string, token: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options?.headers as Record<string, string> ?? {}),
    },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') ?? '');
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [codeCount, setCodeCount] = useState(1);

  const authenticate = useCallback(async () => {
    try {
      setError(null);
      const data = await adminFetch<{ users: UserRecord[] }>('/api/v1/admin/users', token);
      setUsers(data.users);
      const codeData = await adminFetch<{ codes: InviteCode[] }>('/api/v1/admin/invite-codes', token);
      setCodes(codeData.codes);
      sessionStorage.setItem('admin_token', token);
      setAuthenticated(true);
    } catch {
      setError('Invalid admin token');
      setAuthenticated(false);
    }
  }, [token]);

  const refreshData = useCallback(async () => {
    const [codeData, userData] = await Promise.all([
      adminFetch<{ codes: InviteCode[] }>('/api/v1/admin/invite-codes', token),
      adminFetch<{ users: UserRecord[] }>('/api/v1/admin/users', token),
    ]);
    setCodes(codeData.codes);
    setUsers(userData.users);
  }, [token]);

  const createCodes = async () => {
    await adminFetch('/api/v1/admin/invite-codes', token, {
      method: 'POST',
      body: JSON.stringify({ count: codeCount }),
    });
    await refreshData();
  };

  const deleteCode = async (code: string) => {
    await adminFetch(`/api/v1/admin/invite-codes/${code}`, token, { method: 'DELETE' });
    await refreshData();
  };

  const updateUserStatus = async (userId: string, status: string) => {
    await adminFetch(`/api/v1/admin/users/${userId}`, token, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    await refreshData();
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#f7f9fb] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
              <Shield className="w-6 h-6 text-amber-600" />
            </div>
            <CardTitle>Admin Panel</CardTitle>
            <CardDescription>Enter the admin token to access management features.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && <p className="text-sm text-red-600 text-center">{error}</p>}
            <Input
              type="password"
              placeholder="Admin token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && authenticate()}
              autoFocus
            />
            <Button onClick={authenticate} className="w-full" disabled={!token.trim()}>
              Authenticate
            </Button>
            <div className="text-center">
              <a href="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to Cadence
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f9fb] p-8">
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-amber-600" />
            <h1 className="text-2xl font-bold">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Back to Cadence
            </a>
            <Button variant="outline" size="sm" onClick={() => { sessionStorage.removeItem('admin_token'); setAuthenticated(false); }}>
              Logout
            </Button>
          </div>
        </div>

        {/* Invite Codes */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                <CardTitle>Invite Codes</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Input type="number" min={1} max={50} value={codeCount}
                  onChange={(e) => setCodeCount(parseInt(e.target.value) || 1)}
                  className="w-20 h-9" />
                <Button size="sm" onClick={createCodes}>
                  <Plus className="w-4 h-4 mr-1" /> Generate
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {codes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No invite codes yet</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">Code</th>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">Created</th>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">Status</th>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">Used By</th>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {codes.map((c) => (
                      <tr key={c.code} className="border-t">
                        <td className="px-4 py-2 font-mono font-bold">{c.code}</td>
                        <td className="px-4 py-2 text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-2">
                          <Badge variant="secondary" className={c.used_by ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'}>
                            {c.used_by ? 'Used' : 'Available'}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{c.used_by ?? '—'}</td>
                        <td className="px-4 py-2">
                          {!c.used_by && (
                            <button onClick={() => deleteCode(c.code)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <CardTitle>Users</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No users yet</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">User</th>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">GitHub</th>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">Status</th>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">Joined</th>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">Last Login</th>
                      <th className="px-4 py-2 text-left font-medium text-xs uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-t">
                        <td className="px-4 py-2 flex items-center gap-2">
                          {u.avatar_url && <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-full" />}
                          <span className="font-medium">{u.display_name ?? u.id}</span>
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{u.github_login ?? '—'}</td>
                        <td className="px-4 py-2">
                          <Badge variant="secondary" className={
                            u.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                            u.status === 'suspended' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }>
                            {u.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-2 text-muted-foreground">{u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}</td>
                        <td className="px-4 py-2">
                          {u.id !== 'default_user' && u.status === 'active' && (
                            <button onClick={() => updateUserStatus(u.id, 'suspended')} className="text-xs text-red-500 hover:text-red-700 font-medium">
                              Suspend
                            </button>
                          )}
                          {u.status === 'suspended' && (
                            <button onClick={() => updateUserStatus(u.id, 'active')} className="text-xs text-emerald-600 hover:text-emerald-800 font-medium">
                              Activate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
